import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { stylingApi } from '../lib/api.js';
import type { StylistEvent } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatDuration } from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business スタイリスト行動分析ページ。アカウントID検索でスタイリストのセッション別イベントタイムライン・ターゲットユーザー・フィルタ操作・アイテム選定を時系列表示
 * @graph-connects stylingApi [calls] Styling APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatDuration [calls] 時間フォーマット
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 */
export const Route = createFileRoute('/styling/stylist-activity')({
  component: StylistActivityPage,
});

/** イベント種別のスタイルマッピング。スタイリングコンソールの主要イベントを色分け表示 */
/** @graph-connects none */
const EVENT_STYLES: Record<string, { bg: string; label: string }> = {
  screen_rendered: { bg: 'bg-blue-500/20', label: '画面表示' },
  filter_applied: { bg: 'bg-cyan-500/20', label: 'フィルター' },
  item_added_to_coordinate: { bg: 'bg-green-500/20', label: 'アイテム追加' },
  item_removed_from_coordinate: { bg: 'bg-orange-500/20', label: 'アイテム削除' },
  styling_submitted: { bg: 'bg-purple-500/20', label: 'スタイリング送信' },
  styling_failed: { bg: 'bg-red-500/20', label: '送信失敗' },
};

/**
 * スタイリスト行動分析ページコンポーネント。
 * スタイリングコンソールのスタイリスト個人の行動を時系列で追跡し、
 * セッション別のイベントフロー・操作パターン・所要時間を可視化する。
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business StylistActivityPage定義
 * @graph-connects none
 */
function StylistActivityPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('7d');
  const [accountId, setAccountId] = useState('');
  const [searchId, setSearchId] = useState('');

  const timelineQuery = useQuery({
    queryKey: queryKeys.styling.stylistTimeline(searchId, startDate, endDate),
    queryFn: () => stylingApi.getStylistTimeline(searchId, startDate, endDate),
    enabled: searchId.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  /** セッション別にイベントをグループ化。セッションなしイベントは "no-session" に集約 */
  const sessions = useMemo(() => {
    if (!timelineQuery.data) return [];
    const map = new Map<string, StylistEvent[]>();
    for (const ev of timelineQuery.data.events) {
      const key = ev.stylingSessionId ?? 'no-session';
      if (!map.has(key)) map.set(key, []);
      map.get(key)?.push(ev);
    }
    return Array.from(map.entries()).map(([sessionId, events]) => ({
      sessionId,
      events,
      targetUserId: events.find((e) => e.targetUserId)?.targetUserId ?? null,
      startTime: events[0]?.timestamp,
      lastEvent: events[events.length - 1]?.event,
      totalElapsedMs: events[events.length - 1]?.elapsedTimeMs ?? null,
    }));
  }, [timelineQuery.data]);

  const handleSearch = () => {
    if (accountId.trim()) {
      setSearchId(accountId.trim());
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('styling.stylistActivity.title', 'スタイリスト行動')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">
            {t('styling.stylistActivity.description', 'スタイリストのイベントタイムライン')}
          </p>
        </div>
        <DateRangeSelector
          preset={preset}
          onPresetChange={setPreset}
          startDate={startDate}
          endDate={endDate}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* 検索 */}
      <div className="card p-4 flex gap-3 items-center">
        <input
          type="text"
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          placeholder="アカウントID（例: 1086）"
          className="flex-1 px-3 py-2 rounded-lg border border-[var(--color-border)] bg-transparent text-[var(--color-text-primary)] text-sm placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-primary)]"
        />
        <button
          onClick={handleSearch}
          className="px-4 py-2 rounded-lg bg-[var(--color-accent-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
        >
          検索
        </button>
      </div>

      {/* サマリー */}
      {timelineQuery.data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card animate-fade-in-up">
            <p className="text-xs text-[var(--color-text-muted)] mb-1">合計イベント</p>
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">
              {timelineQuery.data.totalEvents.toLocaleString()}
            </p>
          </div>
          <div className="card animate-fade-in-up">
            <p className="text-xs text-[var(--color-text-muted)] mb-1">セッション数</p>
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">
              {timelineQuery.data.sessionCount.toLocaleString()}
            </p>
          </div>
          <div className="card animate-fade-in-up">
            <p className="text-xs text-[var(--color-text-muted)] mb-1">対象ユーザー数</p>
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">
              {new Set(
                timelineQuery.data.events.map((e) => e.targetUserId).filter(Boolean),
              ).size.toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* セッション別タイムライン */}
      {sessions.map((session) => (
        <div key={session.sessionId} className="card p-4 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {session.targetUserId ? `User: ${String(session.targetUserId)}` : 'セッション'}
              </h3>
              <span className="text-xs text-[var(--color-text-muted)] font-mono">
                {session.sessionId !== 'no-session' ? session.sessionId.slice(0, 20) : '-'}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)]">
              {session.startTime && (
                <span>{new Date(session.startTime).toLocaleString('ja-JP')}</span>
              )}
              {session.totalElapsedMs != null && (
                <span className="font-medium">{formatDuration(session.totalElapsedMs)}</span>
              )}
              <span className="px-2 py-0.5 rounded-full bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]">
                {session.events.length} events
              </span>
            </div>
          </div>

          {/* イベントリスト */}
          <div className="space-y-1">
            {session.events.map((ev, i) => (
              <EventRow key={i} event={ev} />
            ))}
          </div>
        </div>
      ))}

      {/* 空状態 */}
      {!searchId && (
        <div className="card p-12 text-center text-[var(--color-text-muted)]">
          アカウントIDを入力して検索してください
        </div>
      )}
      {timelineQuery.isLoading && (
        <div className="card p-12 text-center text-[var(--color-text-muted)]">
          {t('common.loading', '読み込み中...')}
        </div>
      )}
      {timelineQuery.data && timelineQuery.data.events.length === 0 && (
        <div className="card p-12 text-center text-[var(--color-text-muted)]">
          対象期間のイベントがありません
        </div>
      )}
    </div>
  );
}

/**
 * タイムライン内の個別イベント行。イベント種別に応じたバッジ色、
 * 経過時間、ターゲットユーザー、主要プロパティを表示する。
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business EventRow定義
 * @graph-connects none
 */
function EventRow({ event: ev }: { event: StylistEvent }) {
  const style = EVENT_STYLES[ev.event] ?? { bg: 'bg-gray-500/20', label: ev.event };
  const time = new Date(ev.timestamp);
  const timeStr = `${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}:${String(time.getSeconds()).padStart(2, '0')}`;

  /** 表示用プロパティ抽出。styling_type, filter_value, item_sku_id など主要プロパティのみ */
  const propSummary = useMemo(() => {
    if (!ev.properties) return '';
    const keys = Object.keys(ev.properties).filter((k) => !['target_user_id'].includes(k));
    if (keys.length === 0) return '';
    return keys
      .slice(0, 3)
      .map((k) => {
        const v = ev.properties?.[k];
        const val = typeof v === 'string' ? v : JSON.stringify(v);
        const short = val.length > 40 ? val.slice(0, 40) + '...' : val;
        return `${k}=${short}`;
      })
      .join('  ');
  }, [ev.properties]);

  return (
    <div className="flex items-center gap-3 py-1 text-xs hover:bg-[var(--color-bg-hover)] rounded px-2 -mx-2">
      <span className="text-[var(--color-text-muted)] font-mono w-16 shrink-0">{timeStr}</span>
      <span
        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${style.bg} w-28 text-center shrink-0`}
      >
        {style.label}
      </span>
      {ev.elapsedTimeMs != null && ev.elapsedTimeMs > 0 && (
        <span className="text-[var(--color-text-muted)] w-16 text-right shrink-0">
          {formatDuration(ev.elapsedTimeMs)}
        </span>
      )}
      <span className="text-[var(--color-text-secondary)] truncate">{propSummary}</span>
    </div>
  );
}
